import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Database, Bot, Users, Mail, Workflow } from 'lucide-react'
import { AdminStatus } from '@/components/AdminStatus'
import { useEffect, useState } from 'react'
import { adminApi } from '@/lib/api'

interface DashboardStats {
  users: {
    total: number
    active: number
    activeLabel: string
  }
  agents: {
    total: number
  }
  mailingList: {
    total: number
    active: number
    activeLabel: string
  }
  workflows: {
    total: number
    active: number
    activeLabel: string
  }
  conversations: {
    total: number
    active: number
    activeLabel: string
  }
}

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        const data = await adminApi.getDashboardStats()
        setStats(data)
        setError(null)
      } catch (err: any) {
        console.error('Failed to fetch dashboard stats:', err)
        setError(err.message || 'Failed to load dashboard statistics')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const statCards = stats ? [
    {
      title: 'Users',
      value: stats.users.total.toLocaleString(),
      description: `${stats.users.active.toLocaleString()} ${stats.users.activeLabel}`,
      icon: Users,
      color: 'text-purple-600'
    },
    {
      title: 'Agents',
      value: stats.agents.total.toLocaleString(),
      description: 'Configured agents',
      icon: Bot,
      color: 'text-green-600'
    },
    {
      title: 'Mailing List',
      value: stats.mailingList.total.toLocaleString(),
      description: `${stats.mailingList.active.toLocaleString()} ${stats.mailingList.activeLabel}`,
      icon: Mail,
      color: 'text-blue-600'
    },
    {
      title: 'Workflows',
      value: stats.workflows.total.toLocaleString(),
      description: `${stats.workflows.active.toLocaleString()} ${stats.workflows.activeLabel}`,
      icon: Workflow,
      color: 'text-orange-600'
    },
    {
      title: 'Conversations',
      value: stats.conversations.total.toLocaleString(),
      description: `${stats.conversations.active.toLocaleString()} ${stats.conversations.activeLabel}`,
      icon: Database,
      color: 'text-indigo-600'
    }
  ] : []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Welcome to the Ponder Admin Panel. Manage your platform from here.
        </p>
      </div>

      {/* Error State */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && !stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {statCards.map((stat) => (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <CardDescription className="text-xs text-muted-foreground">
                  {stat.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common administrative tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <h3 className="font-medium mb-2">Create Knowledge Base</h3>
              <p className="text-sm text-gray-600">Set up a new knowledge base with schema</p>
            </div>
            <div className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <h3 className="font-medium mb-2">Add Agent</h3>
              <p className="text-sm text-gray-600">Configure a new AI agent</p>
            </div>
            <div className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
              <h3 className="font-medium mb-2">Manage Users</h3>
              <p className="text-sm text-gray-600">View and moderate user accounts</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Admin Status */}
        <AdminStatus />
        
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest system events and changes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Knowledge Base "Product Docs" updated</p>
                  <p className="text-xs text-gray-500">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">New user registered: john@example.com</p>
                  <p className="text-xs text-gray-500">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Agent "Support Bot" configuration changed</p>
                  <p className="text-xs text-gray-500">6 hours ago</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}